import { useMemo } from 'react';
import { useQuery } from 'react-query';

import { useSubscription } from '@app/features/subscription/hooks/subscription';
import SubscriptionStatus from '@app/features/subscription/types/SubscriptionStatus';
import useMemberPermissions from '@app/hooks/useMemberPermission';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { useCurrentMemberGroupList, useCurrentUserMemberId } from '@app/hooks/workspace';
import getAllList from '@app/libs/getAllList';
import { getNetPermission } from '@app/libs/permission/getNetPermission';
import { getDraftRoles } from '@app/store/transport-layer/form/drafts/roles';

type DraftPermission = {
  isLoading: boolean;
  hasDraftAuthority: boolean;
  readAllDraftPermission: { isPermitted: boolean; message: string };
  manageDraftPermission: { isPermitted: boolean; message: string };
};
type DraftDetailPermission = DraftPermission & {
  readablePermission: { isPermitted: boolean; message: string };
  editablePermission: { isPermitted: boolean; message: string };
};

export function useDraftPermission(): DraftPermission;
export function useDraftPermission({ draftId }: { draftId: string }): DraftDetailPermission;
export function useDraftPermission({ draftId }: { draftId?: string } = {}) {
  // 요금제 권한 판단
  const workspaceId = useWorkspaceId();
  const subscription = useSubscription();
  const hasActiveSubscription = subscription.data?.status === SubscriptionStatus.ACTIVE;

  // 워크스페이스 역할 판단
  const { permissionMap, isLoading } = useMemberPermissions();
  const readAllDraftPermission = getNetPermission({
    hasAuthorityPermission: hasActiveSubscription,
    hasRolePermission: permissionMap.READ_ALL_FORM_DRAFT,
  });
  const currentUserMemberId = useCurrentUserMemberId();
  const manageDraftPermission = getNetPermission({
    hasAuthorityPermission: hasActiveSubscription,
    hasRolePermission: permissionMap.MANAGE_FORM_DRAFT,
  });

  // 리소스 역할 판단
  const currentMemberGroups = useCurrentMemberGroupList();
  const draftRoles = useQuery({
    queryKey: [
      {
        path: ['workspaces', workspaceId, 'drafts', draftId, 'roles'],
        type: 'query',
        service: 'form',
        input: {
          workspaceId,
          draftId: draftId ?? '',
          params: {
            subjects: [
              `MEMBER/${currentUserMemberId}` as const,
              ...currentMemberGroups.map<`GROUP/${string}`>((g) => `GROUP/${g.id}` as const),
            ],
          },
        },
      },
    ],
    queryFn: ({ queryKey: [{ input }] }) =>
      getAllList({
        queryFn: async (offset, limit) => {
          const { count, roles } = await getDraftRoles(input.workspaceId, input.draftId, {
            offset,
            limit,
            subjects: input.params.subjects,
          });
          return {
            count,
            list: roles,
          };
        },
      }),
    enabled: !!workspaceId && !!draftId && !!currentUserMemberId,
    staleTime: 5 * 1000,
  });
  const hasViewerRole = useMemo(
    () => draftRoles.data?.some((role) => role.name === 'VIEWER') ?? false,
    [draftRoles.data]
  );
  const hasEditorRole = useMemo(
    () => draftRoles.data?.some((role) => role.name === 'EDITOR') ?? false,
    [draftRoles.data]
  );
  const readablePermission = getNetPermission({
    hasAuthorityPermission: hasActiveSubscription,
    hasRolePermission: readAllDraftPermission.isPermitted || hasViewerRole,
  });
  const editablePermission = getNetPermission({
    hasAuthorityPermission: hasActiveSubscription,
    hasRolePermission: manageDraftPermission.isPermitted || hasEditorRole,
  });

  if (draftId) {
    return {
      isLoading: isLoading || subscription.isLoading || draftRoles.isLoading,
      hasDraftAuthority: hasActiveSubscription,
      readAllDraftPermission,
      manageDraftPermission,
      readablePermission,
      editablePermission,
    } as DraftDetailPermission;
  }

  return {
    isLoading: isLoading || subscription.isLoading,
    hasDraftAuthority: hasActiveSubscription,
    readAllDraftPermission,
    manageDraftPermission,
  };
}
